import { ReactElement, useState } from 'react'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Stack, Tab, Typography, styled } from '@mui/material'
import { IFCParseAsWallOpeningForm } from '@editor/components/IFCElementForm/components'
import IFCSelectionInfo from '../IFCSelectionInfo'
import AutoOpeningImportTab from './AutoOpeningImportTab'

/** Sub-tabs of the opening import: automatic wall inference vs. manual wall picking. */
type OpeningImportTab = 'automatic' | 'advanced'

const StyledTabPanel = styled(TabPanel)({ padding: 0 })

interface Props {
  /** Selected IFC opening element guids. */
  guids: string[]
  projectId: string
  activeGlbFile: FileItem | null
  onSuccess: () => void
  /** Render the selection info inside each tab (used by the guided import step). */
  showSelectionInfo?: boolean
}

/**
 * "Automatisch" / "Erweitert" tab pair for importing IFC wall openings: the automatic tab infers
 * the target wall of every selected opening from its geometry and imports them with one request,
 * the advanced tab lets the user pick the target wall manually (`IFCParseAsWallOpeningForm`).
 * Shared between the guided IFC import step (`IFCOpeningImportPanel`) and the free
 * "Element importieren" flow (`IFCElementForm`).
 */
const OpeningImportModeTabs = ({
  guids,
  projectId,
  activeGlbFile,
  onSuccess,
  showSelectionInfo = false,
}: Props): ReactElement => {
  const [activeTab, setActiveTab] = useState<OpeningImportTab>('automatic')

  // Frontend shows the GLB (converted from IFC); the parse task needs the raw IFC file id = parent.
  const ifcFileId = activeGlbFile?.parent_guid ?? null

  return (
    <TabContext value={activeTab}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <TabList
          onChange={(_event, value: OpeningImportTab) => setActiveTab(value)}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
          }}
        >
          <Tab label="Automatisch" value="automatic" data-cy="tab-opening-import-automatic" />
          <Tab label="Erweitert" value="advanced" data-cy="tab-opening-import-advanced" />
        </TabList>
      </Box>

      <StyledTabPanel value="automatic">
        <Stack spacing={2} sx={{ pt: 2 }}>
          {showSelectionInfo && <IFCSelectionInfo />}

          <AutoOpeningImportTab
            guids={guids}
            projectId={projectId}
            activeGlbFile={activeGlbFile}
            onSuccess={onSuccess}
          />
        </Stack>
      </StyledTabPanel>

      <StyledTabPanel value="advanced">
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Wählen Sie im Modell die Öffnungen, die beim automatischen Wandimport nicht übernommen
            wurden, und importieren Sie sie als „Wandöffnung“ in die zugehörige Wand.
          </Typography>

          {showSelectionInfo && <IFCSelectionInfo />}

          <IFCParseAsWallOpeningForm
            guids={guids}
            ifcFileId={ifcFileId}
            projectId={projectId}
            onSuccess={onSuccess}
          />
        </Stack>
      </StyledTabPanel>
    </TabContext>
  )
}

export default OpeningImportModeTabs
